import { decodeHTML } from "entities";

// Text helpers that run on the server (preview computation) and in clients (search,
// mute). No DOM: they must work in the Convex runtime as well as the browser.

export const decodeEntities = (s: string): string => decodeHTML(s);

/** Visible text of an HTML fragment: tags, scripts and styles removed, entities decoded, whitespace collapsed. */
export function htmlToText(html: string): string {
  return decodeEntities(
    html
      .replace(/<(script|style|noscript|template)[\s\S]*?<\/\1>/gi, " ")
      .replace(/<br\s*\/?>|<\/(p|div|li|h[1-6]|blockquote|figcaption)>/gi, " ")
      .replace(/<[^>]+>/g, " "),
  ).replace(/\s+/g, " ").trim();
}

const DATELINE =
  /(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}\s+/;

/**
 * Magazine feeds open with the headline again, credits and a dateline before the
 * first sentence; keep the sentence. Also drops a trailing "( Comments : N )".
 */
export function tidyLead(t: string): string {
  t = t.replace(/\(\s*Comments\s*:\s*\d+\s*\)\s*$/i, "").trim();
  const m = t.slice(0, 280).match(DATELINE);
  const pre = m ? t.slice(0, m.index) : "";
  if (m && m.index !== undefined && (!pre || (pre.length >= 15 && !/[a-z]{2}[.!?]\s/.test(pre)))) {
    return t.slice(m.index + m[0].length).trim();
  }
  const caps = t.match(/^(?:[A-Z0-9][A-Z0-9'’&:,.\-–—]+\s+){4,}/); // a headline repeated in capitals
  return (caps && caps[0].length >= 20 ? t.slice(caps[0].length) : t).trim();
}

const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

/** The preview stored with an article: tidied text, empty when it only repeats the title. */
export function makePreview(bodyOrSummaryHtml: string, title: string, max = 360): string {
  let t = tidyLead(htmlToText(bodyOrSummaryHtml));
  t = t.replace(/\(\s*Photos?:[^)]*\)/i, "").replace(/\s*Source\s*$/, "").trim();
  if (t.length < 12 || norm(t) === norm(title)) return "";
  if (t.length > max) t = t.slice(0, t.lastIndexOf(" ", max) > max * 0.6 ? t.lastIndexOf(" ", max) : max).trimEnd() + "…";
  return t;
}

/** Search: titles and preview text, case-insensitive. Queries shorter than 2 characters match nothing. */
export function matchesQuery(a: { title: string; preview: string }, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return false;
  return a.title.toLowerCase().includes(q) || a.preview.toLowerCase().includes(q);
}

/** At most this many muted words; the server refuses a longer list. */
export const MAX_MUTES = 100;
/** The note about the limit appears when this few places are left. */
const MUTES_NEARLY_FULL = 10;

/** A word to mute as it is kept: trimmed, lower case, three characters at least; null when too short. */
export function muteWord(raw: string): string | null {
  const w = raw.trim().toLowerCase();
  return w.length >= 3 ? w : null;
}

/** What to tell someone adding muted words: nothing until the list is nearly full, then how full, then that it is. */
export function muteNote(count: number): string | null {
  if (count >= MAX_MUTES) return `Up to ${MAX_MUTES} muted words · stop muting one first`;
  return count >= MAX_MUTES - MUTES_NEARLY_FULL ? `${count} of ${MAX_MUTES} muted words` : null;
}

/** Muted words: a phrase (3+ characters) found in the title or preview hides the article. */
export function mutedBy(a: { title: string; preview: string }, phrases: readonly string[]): string | null {
  const t = a.title.toLowerCase(), p = a.preview.toLowerCase();
  for (const ph of phrases) {
    const q = muteWord(ph);
    if (q && (t.includes(q) || p.includes(q))) return ph;
  }
  return null;
}

/**
 * A post a site's shop or a sponsor publishes as an article (Boing Boing's Shop and its deals):
 * hidden like a muted word, by the byline or a "Sponsored:" title.
 */
export function isSponsored(a: { author?: string | null; title: string }): boolean {
  return (!!a.author && /\b(?:shop|store|deals|sponsored|sponsor|partner content|stackcommerce|brandpoint)\b/i.test(a.author)) ||
    /^(?:sponsored|promoted|advertisement|partner content)\s*[:|–—-]/i.test(a.title);
}

// ---------- Unfollowing ----------

/**
 * What unfollowing a source takes away, for the question that asks first: its unsaved articles
 * leave (and how many of those are unread); saved ones stay. `unreadLeaving` counts only
 * unsaved unread articles, never the saved unread articles that stay.
 */
export function unfollowNote(c: { total: number; saved: number; unreadLeaving: number }): string {
  const leaving = Math.max(0, c.total - c.saved);
  const unread = Math.min(c.unreadLeaving, leaving);
  const stay = c.saved ? ` ${c.saved} saved ${c.saved === 1 ? "stays" : "stay"}.` : "";
  if (!leaving) return `It has no articles to remove.${stay}`;
  return `Its ${leaving} ${leaving === 1 ? "article" : "articles"}${unread ? ` (${unread} unread)` : ""} ${leaving === 1 ? "leaves" : "leave"} your lists.${stay}`;
}

// ---------- Categories ----------

/** The category for sources that have none: always listed last. */
export const UNCATEGORIZED = "Uncategorized";

/**
 * Categories in the owner's order: those placed by dragging first, as placed; any not placed
 * yet after them, alphabetically; Uncategorized last.
 */
export function orderCategories(names: readonly string[], order: readonly string[]): string[] {
  const at = (n: string) => { const i = order.indexOf(n); return i < 0 ? Infinity : i; };
  return [...new Set(names)].sort((a, b) =>
    a === UNCATEGORIZED ? 1 : b === UNCATEGORIZED ? -1 : at(a) - at(b) || (at(a) === Infinity ? a.localeCompare(b) : 0));
}

// ---------- Topics: what the owner follows across every source ----------
// A topic is one name, or several separated by commas ("Hardline, Red Bull Hardline,
// Dan Atherton"): an article naming any of them is under it. Its row shows the first.

/** At most this many topics: each is a sidebar row and a scan of every new article. */
export const MAX_TOPICS = 20;
/** At most this many names in one topic. */
export const MAX_TOPIC_NAMES = 6;

const tidyName = (n: string) => n.replace(/\s+/g, " ").trim();
const usableName = (n: string) => n.length >= 2 && n.length <= 40 && /[\p{L}\p{N}]/u.test(n);

/**
 * A topic as the owner wrote it, tidied: each name's spaces collapsed, names that are too short
 * or repeated dropped, at most six, joined by ", ". Null when no name is usable.
 */
export function topicName(raw: string): string | null {
  const names: string[] = [];
  for (const n of raw.split(",").map(tidyName)) {
    if (usableName(n) && !names.some((x) => x.toLowerCase() === n.toLowerCase())) names.push(n);
  }
  return names.length ? names.slice(0, MAX_TOPIC_NAMES).join(", ") : null;
}

/** What a topic is filed and matched by: its names in lower case. "Hardline" and "hardline" are one topic. */
export const topicKey = (name: string): string =>
  name.split(",").map((n) => tidyName(n).toLowerCase()).filter(Boolean).join(", ");

/** What a topic's row and list are called: its first name. */
export const topicLabel = (name: string): string => tidyName(name.split(",")[0] ?? name);

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * A topic's pattern: any of its names, each word whole, in any case, with any spacing between
 * words, and their plural or possessive ("e-bike" finds "E-bikes" and "e-bike's", not "e-biker";
 * "wind" never "window"). Longer names first, so "Red Bull Hardline" wins over "Hardline".
 */
function topicRe(key: string): RegExp {
  const names = key.split(", ").sort((a, b) => b.length - a.length).map((n) => n.split(" ").map(escapeRe).join("\\s+"));
  return new RegExp(`(?<![\\p{L}\\p{N}])(?:${names.join("|")})(?:e?s|['’]s)?(?![\\p{L}\\p{N}])`, "iu");
}

const topicRes = new Map<string, RegExp>();
const reFor = (key: string) => {
  let re = topicRes.get(key);
  if (!re) topicRes.set(key, (re = topicRe(key)));
  return re;
};

/** The topics (keys) an article's text names: its title, preview and the start of its text (the search text). */
export function topicsIn(text: string, keys: readonly string[]): string[] {
  return keys.filter((k) => reFor(k).test(text));
}

/** Where a topic was found outside an article's title: the passage, and where the name sits in it. */
export interface TopicMatch { topic: string; text: string; start: number; end: number }

/**
 * Why an article is under a topic, when its title does not say: the first place the preview or
 * text names it, as a passage of a line or so with the name's position (for a highlight).
 * [text] is the search text, which begins with the title. Null when the title names it.
 */
export function topicMatch(title: string, text: string, key: string): TopicMatch | null {
  const re = reFor(key);
  if (re.test(title)) return null;
  const rest = text.slice(text.startsWith(title) ? title.length : 0).replace(/\s+/g, " ");
  const m = re.exec(rest);
  if (!m) return null;
  const at = m.index, len = m[0].length;
  let from = Math.max(0, at - 50);
  if (from > 0) from = rest.indexOf(" ", from) + 1 || from;
  let to = Math.min(rest.length, at + len + 110);
  if (to < rest.length) { const sp = rest.lastIndexOf(" ", to); if (sp > at + len) to = sp; }
  const lead = from > 0 ? "…" : "", tail = to < rest.length ? "…" : "";
  const passage = lead + rest.slice(from, to).trim() + tail;
  const start = lead.length + (at - from) - (rest.slice(from, to).length - rest.slice(from, to).trimStart().length);
  return { topic: key, text: passage, start, end: start + len };
}

/** A short excerpt around a match, for search results. */
export function snippet(text: string, query: string, before = 36, after = 56): string {
  const q = query.trim().toLowerCase();
  const i = text.toLowerCase().indexOf(q);
  if (!q || i < 0) return "";
  const st = Math.max(0, text.lastIndexOf(" ", Math.max(0, i - before)) + 1);
  return (st ? "…" : "") + text.slice(st, i + q.length + after).trim() + "…";
}
